/*
  VBRNTT city scroller.

  The server renders every upcoming card and every pill, so the page is
  complete without JS. This script only:
  - sorts cards soonest-first (Liquid can't sort metaobjects by a date field)
  - re-checks expiry, because the rendered page may be cached past an event
  - hides pills whose panel emptied, and the rail if nothing is left
  - filters in place when a pill is clicked, keeping real links for
    crawlers, Cmd/Ctrl-click and middle-click
  - applies the same filter to the poster carousel, if the section has one
    (vbrntt-poster-carousel.js owns the carousel's own behaviour)

  Pills are <a> when there's a chapter or fair page to link to, and
  <button> when there isn't (a city with events but no chapter entry yet).
  Both filter the same way; links carry aria-current, buttons aria-pressed.

  Events page (a tab row is present): category tabs over the city row
  (All events | Book Club | Book Fairs | Business | Other; Kendall,
  2026-10-21). In the code the tabs are still "brand" tabs (data-brand-pill,
  this.brand); each card's data-brand is its category key, worked out by
  the section (the type's category, else its brand's).
  - The city rail rebuilds per category: a city pill shows only if that
    category has upcoming events there. If the selected city has none under
    the new category, it falls back to all cities rather than an empty panel.
  - The band and card field take the tab's colours (a brand's pair, or the
    VBRNTT pair). The change animates only without prefers-reduced-motion.
  - The hash carries both: "#book-club/salt-lake-city", trailing "all"s
    dropped. No remembered selection here (Emmy, 2026-10-20): a visitor from
    the menu always lands on All events / all cities. The book-club and
    book-fairs pages keep their remembered city.
  - Entry points without a hash: ?category= (handleized category), ?brand=
    (a brand handle, mapped through the tab that takes its colours), ?city=
    (a location handle) and ?type= (an event_type handle: opens its
    category's tab, listed in the tab's data-types). The hash wins if
    present; the first filter change drops them for the hash.

  Card cap (data-cap, a theme-editor setting; 0 = off): after each filter
  change only the first N visible cards show, plus a "Show All Events"
  button that reveals the rest in place and moves focus to the first one it
  revealed. Capped cards carry data-over-cap rather than hidden, so the
  filter's own hidden state is never touched. Without JS every card shows.

  Expiry rule: a card is hidden when Date.now() >= data-expires * 1000.
  data-expires is the end of the event's last day in shop time. Never
  compare it against the visitor's local start-of-day.
*/
(() => {
  if (customElements.get('vbrntt-city-scroller')) return;

  const ALL = 'all';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        // Storage unavailable (private mode, blocked). Selection just won't persist.
      }
    },
  };

  const readHash = () => {
    const raw = window.location.hash.slice(1);
    if (!raw) return '';
    try {
      return decodeURIComponent(raw);
    } catch (error) {
      return raw;
    }
  };

  // Events page: "brand/city/type", shorter forms, or "" -> { brand, city, type }.
  const parsePair = (value) => {
    if (!value) return null;
    const [brand, city, type] = value.split('/');
    return { brand: brand || ALL, city: city || ALL, type: type || ALL };
  };

  const ENTRY_PARAMS = ['category', 'brand', 'city', 'type'];
  const readParam = (name) => {
    try {
      return new URLSearchParams(window.location.search).get(name) || '';
    } catch (error) {
      return '';
    }
  };

  class VbrnttCityScroller extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-rail]');
      this.brandRail = this.querySelector('[data-brand-rail]');
      this.cardList = this.querySelector('[data-cards]');
      this.statusEl = this.querySelector('[data-status]');
      this.emptyEl = this.querySelector('[data-empty]');
      this.cityEmptyEl = this.querySelector('[data-city-empty]');
      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.carousel = this.querySelector('vbrntt-poster-carousel');
      this.pills = Array.from(this.querySelectorAll('[data-pill]'));
      this.brandPills = this.brandRail ? Array.from(this.brandRail.querySelectorAll('[data-brand-pill]')) : [];
      this.type = ALL;
      // Events page: hash only, never storage.
      this.storageKey = this.brandRail ? '' : this.dataset.storageKey || '';
      this.cap = Number(this.dataset.cap) || 0;
      this.moreButton = this.querySelector('[data-more]');
      this.uncappedFor = null;
      this.selected = ALL;
      this.brand = ALL;
      this.counts = new Map();

      this.onClick = this.onClick.bind(this);
      this.onBrandClick = this.onBrandClick.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onHashChange = this.onHashChange.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onMoreClick = this.onMoreClick.bind(this);
      this.updateFade = this.updateFade.bind(this);

      // Rails that scroll sideways and fade at the trailing edge.
      this.fadeRails = [this.rail].filter(Boolean);
      if (this.rail) this.rail.addEventListener('click', this.onClick);
      this.fadeRails.forEach((rail) => {
        rail.addEventListener('focusin', this.onFocusIn);
        rail.addEventListener('scroll', this.updateFade, { passive: true });
      });
      if (this.fadeRails.length && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateFade);
        this.fadeRails.forEach((rail) => this.resizeObserver.observe(rail));
      }
      if (this.brandRail) this.brandRail.addEventListener('click', this.onBrandClick);
      if (this.moreButton) this.moreButton.addEventListener('click', this.onMoreClick);
      window.addEventListener('hashchange', this.onHashChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.sortCards();
      this.refresh(true);
    }

    disconnectedCallback() {
      if (this.rail) this.rail.removeEventListener('click', this.onClick);
      this.fadeRails.forEach((rail) => {
        rail.removeEventListener('focusin', this.onFocusIn);
        rail.removeEventListener('scroll', this.updateFade);
      });
      if (this.brandRail) this.brandRail.removeEventListener('click', this.onBrandClick);
      if (this.moreButton) this.moreButton.removeEventListener('click', this.onMoreClick);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('hashchange', this.onHashChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    /* Soonest first; ties broken by expiry, then server order. this.cards
       follows the display order too, which the card cap relies on. */
    sortCards() {
      if (!this.cardList) return;
      this.cards = this.cards
        .map((card, index) => ({
          card,
          index,
          start: Number(card.dataset.start) || 0,
          expires: Number(card.dataset.expires) || 0,
        }))
        .sort((a, b) => a.start - b.start || a.expires - b.expires || a.index - b.index)
        .map(({ card }) => card);
      this.cards.forEach((card) => this.cardList.appendChild(card));
    }

    /* Keeps "All" first; orders the rest alphabetically by label ("Boise, ID",
       "Davis & Weber County, UT", ...) on every rail, the fairs rail included.
       Soonest-first was retired 2026-10-16. The server renders the same order. */
    sortPills() {
      if (!this.rail) return;
      const byLabel = (a, b) =>
        a.textContent.trim().localeCompare(b.textContent.trim(), undefined, { sensitivity: 'base' });

      this.pills
        .filter((pill) => pill.dataset.key !== ALL)
        .sort(byLabel)
        .forEach((pill) => this.rail.appendChild(pill.closest('li')));
    }

    inBrand(card) {
      return this.brand === ALL || card.dataset.brand === this.brand;
    }

    /* Re-checks expiry and rebuilds which brand and city pills are available. */
    recount() {
      const now = Date.now();
      const counts = new Map();
      const brandCounts = new Map();
      let total = 0;
      let brandTotal = 0;

      this.cards.forEach((card) => {
        const expired = now >= Number(card.dataset.expires) * 1000;
        card.toggleAttribute('data-expired', expired);
        if (expired) return;
        total += 1;
        if (card.dataset.brand) brandCounts.set(card.dataset.brand, (brandCounts.get(card.dataset.brand) || 0) + 1);
        if (!this.inBrand(card)) return;
        brandTotal += 1;
        const key = card.dataset.key;
        if (!key) return;
        counts.set(key, (counts.get(key) || 0) + 1);
      });

      this.counts = counts;

      this.brandPills.forEach((pill) => {
        const key = pill.dataset.key;
        pill.closest('li').hidden = key === ALL ? total === 0 : !brandCounts.has(key);
      });
      if (this.brandRail) this.brandRail.closest('nav').hidden = total === 0;

      this.pills.forEach((pill) => {
        const key = pill.dataset.key;
        // data-always: a city set to always show (Events page; Book Club page since 2026-10-29), with or without events.
        const available = key === ALL ? brandTotal > 0 : counts.has(key) || pill.hasAttribute('data-always');
        pill.closest('li').hidden = !available;
      });
      this.sortPills();

      const nav = this.rail && this.rail.closest('nav');
      // Always-show cities keep the rail up even with nothing booked.
      if (nav) nav.hidden = brandTotal === 0 && !this.pills.some((pill) => pill.hasAttribute('data-always'));
      if (this.emptyEl) this.emptyEl.hidden = brandTotal > 0;
    }

    refresh(initial) {
      if (initial) {
        const start = this.resolveInitial();
        this.applyBrand(start.brand);
        this.type = start.type || ALL;
        this.recount();
        this.select(this.isAvailable(start.city) ? start.city : ALL, { write: false, announce: false, smooth: false });
        return;
      }
      if (this.brandRail && this.brand !== ALL && !this.brandAvailable(this.brand)) this.applyBrand(ALL);
      this.recount();
      this.select(this.isAvailable(this.selected) ? this.selected : ALL, { write: false, announce: false, smooth: false });
    }

    /* A type in a link (?type=, or a third hash part from older links) opens its
       category's tab; there's no type filter of its own. */
    mapBrandType(pick) {
      if (pick.type === ALL) return pick;
      const tab = this.brandPills.find((pill) => (pill.dataset.types || '').split(' ').includes(pick.type));
      if (tab && pick.brand === ALL) return { ...pick, brand: tab.dataset.key, type: ALL };
      return { ...pick, type: ALL };
    }

    /* URL hash -> last selection for this rail -> "all".
       Events page: hash -> ?category= / ?brand= / ?city= / ?type= -> "all". An
       unknown category or type opens All events; an unknown city falls back to all. */
    resolveInitial() {
      if (this.brandRail) {
        const fromHash = parsePair(readHash());
        const tab = this.brandPills.find((pill) => (pill.dataset.category && pill.dataset.category === readParam('category'))
          || (pill.dataset.brandHandle && pill.dataset.brandHandle === readParam('brand')));
        const fromQuery = { brand: tab ? tab.dataset.key : ALL, city: readParam('city') || ALL, type: readParam('type') || ALL };
        const pick = this.mapBrandType(fromHash || fromQuery);
        if (pick.brand === ALL || this.brandAvailable(pick.brand)) return pick;
        return { brand: ALL, city: ALL, type: ALL };
      }
      const fromHash = readHash();
      if (fromHash && this.isAvailable(fromHash)) return { brand: ALL, city: fromHash };
      const stored = this.storageKey ? storage.get(this.storageKey) : null;
      if (stored && this.isAvailable(stored)) return { brand: ALL, city: stored };
      return { brand: ALL, city: ALL };
    }

    brandAvailable(key) {
      if (key === ALL) return true;
      const now = Date.now();
      return this.cards.some((card) => card.dataset.brand === key && now < Number(card.dataset.expires) * 1000)
        && this.brandPills.some((pill) => pill.dataset.key === key);
    }

    isAvailable(key) {
      if (key === ALL) return true;
      const pill = this.pillFor(key);
      if (pill && pill.hasAttribute('data-always')) return true;
      // Before the first recount, count against the current brand directly.
      if (!this.counts.size) {
        const now = Date.now();
        return this.cards.some((card) => card.dataset.key === key && this.inBrand(card) && now < Number(card.dataset.expires) * 1000)
          && Boolean(this.pillFor(key));
      }
      return this.counts.has(key) && Boolean(this.pillFor(key));
    }

    pillFor(key) {
      return this.pills.find((pill) => pill.dataset.key === key) || null;
    }

    /* Sets the brand: its pill state and the band/field colours (its own pair). */
    applyBrand(key) {
      this.brand = key;
      let active = null;
      this.brandPills.forEach((pill) => {
        const on = pill.dataset.key === key;
        if (on) active = pill;
        if (on) pill.setAttribute('aria-current', 'true');
        else pill.removeAttribute('aria-current');
      });
      if (active && active.dataset.accent) {
        this.style.setProperty('--vbrntt-accent', active.dataset.accent);
        this.style.setProperty('--vbrntt-on-accent', active.dataset.onAccent);
      }
    }

    select(key, { write, announce, smooth }) {
      this.selected = key;

      this.cards.forEach((card) => {
        const expired = card.hasAttribute('data-expired');
        card.hidden = expired || !this.inBrand(card) || (key !== ALL && card.dataset.key !== key);
      });

      // Poster carousel: same brand/city/expiry rule as the cards. The carousel
      // then picks its own mode (hidden, still row, rotating) from what's left.
      if (this.slides.length) {
        const now = Date.now();
        this.slides.forEach((slide) => {
          const expired = now >= Number(slide.dataset.expires) * 1000;
          slide.hidden = expired || !this.inBrand(slide) || (key !== ALL && slide.dataset.key !== key);
        });
        if (this.carousel) this.carousel.dispatchEvent(new CustomEvent('vbrntt:posters-filtered'));
      }

      let activePill = null;
      this.pills.forEach((pill) => {
        const on = pill.dataset.key === key;
        if (on) activePill = pill;
        if (pill.tagName === 'BUTTON') {
          pill.setAttribute('aria-pressed', String(on));
        } else if (on) {
          pill.setAttribute('aria-current', 'true');
        } else {
          pill.removeAttribute('aria-current');
        }
      });

      // A city set to always show, picked with nothing upcoming: "check back soon" instead of an empty list.
      if (this.cityEmptyEl) this.cityEmptyEl.hidden = key === ALL || this.cards.some((card) => !card.hidden);
      // Book Club page: "Meet your host" goes to the picked city's chapter page, when its pill links to one.
      const cityLink = this.cityEmptyEl && this.cityEmptyEl.querySelector('[data-city-empty-link]');
      if (cityLink) {
        const href = activePill && activePill.tagName === 'A' ? activePill.getAttribute('href') : '';
        cityLink.hidden = !href;
        if (href) cityLink.setAttribute('href', href);
      }

      this.applyCap();
      if (write) this.write();
      if (announce) this.announce(key, activePill);
      if (activePill) this.scrollPillIntoView(activePill, smooth);
      this.updateFade();
    }

    /* Shows the first N visible cards and the button, unless this same
       brand/city was already revealed (a refresh on tab return keeps it open). */
    applyCap() {
      const filter = `${this.brand}/${this.selected}/${this.type}`;
      const open = !this.cap || this.uncappedFor === filter;
      if (!open) this.uncappedFor = null;
      let shown = 0;
      this.cards.forEach((card) => {
        if (card.hidden) {
          card.removeAttribute('data-over-cap');
          return;
        }
        shown += 1;
        card.toggleAttribute('data-over-cap', !open && shown > this.cap);
      });
      if (this.moreButton) this.moreButton.hidden = open || shown <= this.cap;
    }

    onMoreClick() {
      const first = this.cards.find((card) => card.hasAttribute('data-over-cap'));
      this.uncappedFor = `${this.brand}/${this.selected}/${this.type}`;
      this.applyCap();
      if (!first) return;
      const target = first.querySelector('a[href]') || first;
      if (target === first) first.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      first.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    /* Hash, plus storage except on the events page. Events page: "brand/city/type"
       with trailing "all"s dropped; all/all/all writes no hash. */
    write() {
      let value;
      let search = window.location.search;
      if (this.brandRail) {
        const parts = [this.brand, this.selected, this.type];
        while (parts.length && parts[parts.length - 1] === ALL) parts.pop();
        value = parts.join('/');
        // The hash now carries the selection, so entry parameters must not override it on reload.
        const params = new URLSearchParams(search);
        ENTRY_PARAMS.forEach((name) => params.delete(name));
        search = params.toString() ? `?${params}` : '';
      } else {
        value = this.selected === ALL ? '' : this.selected;
      }
      const url = window.location.pathname + search + (value ? `#${value.split('/').map(encodeURIComponent).join('/')}` : '');
      window.history.replaceState(window.history.state, '', url);
      if (this.storageKey) storage.set(this.storageKey, value || ALL);
    }

    announce(key, pill) {
      if (!this.statusEl) return;
      const count = this.cards.filter((card) => !card.hidden).length;
      const name = pill ? pill.textContent.trim() : '';
      let template = key === ALL ? this.dataset.statusAll : this.dataset.statusFiltered;
      if (this.brandRail && this.brand !== ALL && this.dataset.statusBrand) template = this.dataset.statusBrand;
      if (!template) return;
      const brandPill = this.brandPills.find((p) => p.dataset.key === this.brand);
      this.statusEl.textContent = template
        .replace(/\[count\]/g, String(count))
        .replace(/\[name\]/g, name)
        .replace(/\[brand\]/g, brandPill ? brandPill.textContent.trim() : '');
    }

    /* Scrolls the rail only, never the page. */
    scrollPillIntoView(pill, smooth) {
      const rail = pill.closest('ul');
      if (!rail) return;
      const target = pill.offsetLeft - (rail.clientWidth - pill.offsetWidth) / 2;
      const max = rail.scrollWidth - rail.clientWidth;
      rail.scrollTo({
        left: Math.max(0, Math.min(target, max)),
        behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto',
      });
    }

    updateFade() {
      (this.fadeRails || []).forEach((rail) => {
        rail.toggleAttribute('data-fade-end', rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 2);
      });
    }

    ownGesture(event, pill) {
      if (pill.tagName !== 'A') return false;
      return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    }

    onClick(event) {
      const pill = event.target.closest('[data-pill]');
      if (!pill || !this.rail.contains(pill)) return;
      if (event.defaultPrevented) return;
      // On links, let the browser handle new-tab and download gestures.
      // Buttons have nowhere to navigate, so they always filter.
      if (this.ownGesture(event, pill)) return;
      const key = pill.dataset.key;
      if (!this.isAvailable(key)) return;
      event.preventDefault();
      this.select(key, { write: true, announce: true, smooth: true });
    }

    /* Brand tier: switch brand, rebuild the city tier, fall back to all cities if needed. */
    onBrandClick(event) {
      const pill = event.target.closest('[data-brand-pill]');
      if (!pill || event.defaultPrevented || this.ownGesture(event, pill)) return;
      const key = pill.dataset.key;
      if (!this.brandAvailable(key)) return;
      event.preventDefault();
      this.applyBrand(key);
      this.recount();
      const city = this.isAvailable(this.selected) ? this.selected : ALL;
      this.select(city, { write: true, announce: true, smooth: true });
      this.scrollPillIntoView(pill, true);
    }

    /* Keeps a keyboard-focused pill out of the faded trailing edge. */
    onFocusIn(event) {
      const pill = event.target.closest('[data-pill]');
      if (pill) this.scrollPillIntoView(pill, true);
    }

    onHashChange() {
      if (this.brandRail) {
        const parsed = parsePair(readHash());
        const pair = parsed && this.mapBrandType(parsed);
        if (!pair || (pair.brand === this.brand && pair.city === this.selected && pair.type === this.type)) return;
        if (pair.brand !== ALL && !this.brandAvailable(pair.brand)) return;
        this.applyBrand(pair.brand);
        this.type = pair.type;
        this.recount();
        this.select(this.isAvailable(pair.city) ? pair.city : ALL, { write: true, announce: true, smooth: true });
        return;
      }
      const key = readHash();
      if (key && key !== this.selected && this.isAvailable(key)) {
        this.select(key, { write: true, announce: true, smooth: true });
      }
    }

    /* A tab left open past an event's end catches up when it's revisited. */
    onVisibilityChange() {
      if (document.visibilityState === 'visible') this.refresh(false);
    }
  }

  customElements.define('vbrntt-city-scroller', VbrnttCityScroller);
})();
